import {
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip
} from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { SiteContext, StudentContext, UserContext } from '../../contexts';
import * as DTO from '../../dto';
import { QueryType } from '../../helpers/query';
import UserHelper from '../../helpers/userHelper';
import { useRecordOfContacts, useUserPreferences, useUpdateUserPreferences, useRocTypes } from '../../hooks';
import { IconButton, LanguageString } from '../Common';
import { RecordOfContactDialog } from './RecordOfContactDialog';
import { RecordOfContactsTableBody } from './RecordOfContactsTableBody';
import { RecordOfContactPrintDialog } from './RecordOfContactPrintDialog';
import { RecordOfContactFilterDialog } from './RecordOfContactFilterDialog';
import { ROCType } from '../../dto';

function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, function(txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

interface RecordOfContactsProps {
  minDate?: Date;
  maxDate?: Date;
  defaultRowsPerPageIndex?: number;
  filter?: {
    filterTo?: boolean;
    rocTypeId?: number;
  }
  showTitleBar?: boolean;
}

const ROCTableHeaderText: React.FC<{ text: string }> = ({ text }) => {
  switch (text) {
    case 'doc':
      return <LanguageString groupName="RECORD_OF_CONTACT" resourceName="DATE_OF_CONTACT" defaultText="Date of Contact" />;
    case 'toc':
      return <LanguageString groupName="RECORD_OF_CONTACT" resourceName="TYPE_OF_CONTACT" defaultText="Type of Contact" />;
    case 'from':
      return <LanguageString groupName="RECORD_OF_CONTACT" resourceName="CONTACTED_BY" defaultText="Contacted By" />;
    default:
      return (
        <LanguageString groupName="RECORD_OF_CONTACT" resourceName={text} defaultText={toTitleCase(text)} />
      );
  }
};

export const RecordOfContacts: React.FC<RecordOfContactsProps> = props => {
  const rowsPerPageOptions = [5, 10, 25, 50];
  const userContext = useContext(UserContext);
  const studentCtx = useContext(StudentContext);
  const allRocTypes = useRocTypes({ variables: {} });
  const [page, setPage] = useState(0);
  const [selectedRecordOfContact, setSelectedRecordOfContact] = useState<QueryType<DTO.Roc> | undefined>(undefined);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const rocFilterPrefs = useUserPreferences({ variables: { userId: userContext.user?.id ?? 0, keys: [DTO.PreferenceKey.RecordOfContactsFilters] } });
  const [rocFilterTypes, setRocFilterTypes] = useState<number[]>(rocFilterPrefs.data?.userPreference[0]?.value ? JSON.parse(rocFilterPrefs.data.userPreference[0].value) : []);
  const [updatePrefs] = useUpdateUserPreferences();
  const [rowsPerPage, setRowsPerPage] = useState(
    props.defaultRowsPerPageIndex ? rowsPerPageOptions[props.defaultRowsPerPageIndex] : rowsPerPageOptions[rowsPerPageOptions.length - 1]
  );
  let canEdit = false;

  if (userContext.user) canEdit = UserHelper.isAdvisorOrAdmin(userContext.user);

  useEffect(() => {
    if (rocFilterPrefs.data?.userPreference[0]?.value) {
      setRocFilterTypes(JSON.parse(rocFilterPrefs.data.userPreference[0].value));
    }
  }, [rocFilterPrefs.data]);

  const { data: rocData, loading } = useRecordOfContacts({
    variables: {
      userId: studentCtx?.student.id ?? 0,
      limit: rowsPerPage,
      offset: rowsPerPage * page,
      rocTypeIds: rocFilterTypes
    }
  });

  useEffect(() => {
    if (allRocTypes.data?.rocTypes && allRocTypes.data.rocTypes.length > 0 && rocFilterTypes.length <= 0) {
      setRocFilterTypes(allRocTypes.data.rocTypes.map(r => r.id));
    }
  }, [allRocTypes]);

  const resolvedRocs = rocData?.user.resolvedRocs;
  const rtMap: { [rtId: number]: DTO.ROCType } = {};
  resolvedRocs?.rocTypes.forEach(rt => rtMap[rt.id] = rt);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleAddRecordOfContact = () => {
    setSelectedRecordOfContact(undefined);
    setDialogOpen(true);
  };

  const handleSelectedRoc = (roc: DTO.Roc) => {
    setSelectedRecordOfContact(roc);
    setDialogOpen(true);
  };

  const toggleDialog = () => setDialogOpen(!dialogOpen);
  const toggleFilterDialog = () => setFilterDialogOpen(!filterDialogOpen);

  const Wrapper: React.FC = ({ children }) => {
    const siteCtx = useContext(SiteContext);
    const [printDialogOpen, setPrintDialogOpen] = useState(false);
    const toggle = () => setPrintDialogOpen(!printDialogOpen);

    return (
      <Card>
        {(props.showTitleBar == null || props.showTitleBar === true) && (
          <CardHeader
            title={<LanguageString groupName="GENERAL" resourceName="RECORD_OF_CONTACTS_TITLE" defaultText="Record of Contacts" />}
            action={
              <>
                <IconButton aria-label="rocs filter" onClick={toggleFilterDialog} icon='filter_list'/>
                {siteCtx.site?.downloadRocEnabled === true && (
                  <Tooltip title={<LanguageString groupName="GENERAL" resourceName="ROC_TOOLTIP" defaultText="Print ROCs" />}>
                    <IconButton aria-label="print rocs" onClick={toggle} icon="cloud_download" />
                  </Tooltip>
                )}
                {
                  canEdit ? (
                    <Tooltip title="Add Record of Contact">
                      <IconButton aria-label="add record of contact" onClick={handleAddRecordOfContact} icon="add_circle" />
                    </Tooltip>
                  ) : null
                }
              </>
            }
          />
        )}
        <CardContent>{children}</CardContent>
        <RecordOfContactPrintDialog open={printDialogOpen} toggle={toggle} />
        <RecordOfContactFilterDialog
          open={filterDialogOpen}
          close={toggleFilterDialog}
          rocTypes={rocData?.user.resolvedRocs.rocTypes as ROCType[]}
          resetPage={() => setPage(0)}
          filteredIds={rocFilterTypes}
          setFilteredIds={async (na: number[]) => {
            await updatePrefs({
              variables: { input: [{ key: DTO.PreferenceKey.RecordOfContactsFilters, value: JSON.stringify(na) }] },
              refetchQueries: ['GetUserPreferences']
            });
          }}
        />
      </Card>
    );
  };

  if (loading || resolvedRocs?.rocs == null) return <Wrapper><CircularProgress color="primary" /></Wrapper>;
  const rocs = resolvedRocs?.rocs ?? [];
  const rocTypes = resolvedRocs?.rocTypes ?? [];

  const pagination = {
    rowsPerPageOptions,
    component: 'div',
    count: resolvedRocs.rocsCount ?? 0,
    rowsPerPage,
    page,
    onChangeRowsPerPage: handleChangeRowsPerPage
  };

  if (resolvedRocs.columns == null || rtMap == null) return <Wrapper><CircularProgress color="primary" /></Wrapper>;

  return (
    <Wrapper>
      {(loading || rocs == null || rocTypes == null) ? <CircularProgress color="primary" /> :
        <Grid item xs style={{ overflowX: 'auto' }}>
          <Table aria-labelledby="tableTitle" size={'medium'}>
            <TableHead>
              <TableRow>
                {rocData?.user.resolvedRocs.columns.map((q, i) => (
                  <TableCell key={`q-${i}`} align="left">
                    <ROCTableHeaderText text={q} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <RecordOfContactsTableBody rocs={rocs} columns={rocData?.user.resolvedRocs.columns ?? []} selectedRoc={handleSelectedRoc} rocTypes={rocTypes} />
            </TableBody>
          </Table>
        </Grid>
      }
      <TablePagination {...pagination} onChangePage={(_, newPage) => setPage(newPage)} />
      <RecordOfContactDialog open={dialogOpen} toggle={toggleDialog} recordOfContact={selectedRecordOfContact} rocTypes={rocTypes} rtMap={rtMap} />
    </Wrapper>
  );
};
export default RecordOfContacts;
